import { assetsApi } from 'core/api/assets.api';
import { getDateFromMoment } from 'core/constants/globalfunc';
import {
  setAddCart,
  setCartItems,
  setEquipFee,
  setEquipFeeByAssetId,
  setIsLoading,
  setSelectedAddonServiceIdsForAsset,
} from 'core/store/slices/settingSlice';
import { useGetAccessToken } from 'hooks/api';
import React from 'react';
import { useDispatch } from 'react-redux';
import { useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';
import {
  dedupeCartItemsByLocation,
  getMergedEquipFeeForItems,
  getSingleCheckoutItem,
  loadSelectedAddonIdsFromStorage,
  syncSelectedAddonIdsForCartItems,
} from 'utils/cartUtils';

const getCurrentMinutes = () => Math.round(new Date().getTime() / (1000 * 60));

function useCartLoading(dispatch: ReturnType<typeof useDispatch>) {
  const inFlightCountRef = React.useRef(0);

  const beginLoading = React.useCallback(() => {
    inFlightCountRef.current += 1;
    if (inFlightCountRef.current === 1) {
      dispatch(setIsLoading(true));
    }
  }, [dispatch]);

  const endLoading = React.useCallback(() => {
    inFlightCountRef.current = Math.max(0, inFlightCountRef.current - 1);
    if (inFlightCountRef.current === 0) {
      dispatch(setIsLoading(false));
    }
  }, [dispatch]);

  return { beginLoading, endLoading };
}

export const useGetCartItems = () => {
  const dispatch = useDispatch();
  const { beginLoading, endLoading } = useCartLoading(dispatch);
  const { pathname } = useLocation();
  const cartItems = useSelector((state: any) => state.setting.cartItems);
  const filterDate = useSelector((state: any) => state.setting.filterDate);
  const checkoutForm = useSelector((state: any) => state.setting.checkoutForm);
  const selectedCheckoutLocationId = useSelector(
    (state: any) => state.setting.selectedCheckoutLocationId,
  );
  const selectedAddonServiceIdsByAssetId = useSelector(
    (state: any) => state.setting.selectedAddonServiceIdsByAssetId || {},
  );
  const equipFeeByAssetId = useSelector(
    (state: any) => state.setting.equipFeeByAssetId || {},
  );
  const cartAssetIds = React.useRef<string[]>([]);
  const cartAssetIdsMinutes = React.useRef(0);
  const feeRequestIdRef = React.useRef(0);
  const equipFeeByAssetIdRef = React.useRef(equipFeeByAssetId);
  equipFeeByAssetIdRef.current = equipFeeByAssetId;

  const pickupdate = getDateFromMoment(filterDate.pickupDate);
  const pickupTime = filterDate.pickupTime || '';
  const returnDate = getDateFromMoment(filterDate.returnDate);
  const returnTime = filterDate.returnTime || '';
  const discountCode = checkoutForm?.discountCode || '';
  const cartAssetIdList = React.useMemo(
    () => cartItems.map((item: any) => String(item?.assetId)).filter(Boolean),
    [cartItems],
  );
  const selectedServiceIdsKey = React.useMemo(
    () =>
      cartAssetIdList
        .map((assetId: string) => {
          const ids = selectedAddonServiceIdsByAssetId?.[assetId] || [];
          return `${assetId}:${ids.slice().sort((a: number, b: number) => a - b).join(',')}`;
        })
        .join('|'),
    [cartAssetIdList, selectedAddonServiceIdsByAssetId],
  );
  const { data: accessTokenData } = useGetAccessToken();
  const accessToken = accessTokenData?.data?.accessToken;
  const enabled = Boolean(accessToken);

  React.useEffect(() => {
    if (!enabled) return;

    try {
      const localStorageAssetIds = localStorage.getItem('CartAssetIds') || '[]';
      const localStorageAssetIdsTime = localStorage.getItem('CartAssetIdsMinutes');

      cartAssetIds.current = JSON.parse(localStorageAssetIds);
      cartAssetIdsMinutes.current = Number(localStorageAssetIdsTime) || 0;
    } catch (err) {}

    if (cartAssetIdsMinutes.current && getCurrentMinutes() - cartAssetIdsMinutes.current > 60) {
      localStorage.setItem('CartAssetIds', '[]');
      localStorage.setItem('CartAssetIdsMinutes', '0');
      cartAssetIds.current = [];
      cartAssetIdsMinutes.current = 0;
    }

    if (cartAssetIdList.length) {
      cartAssetIds.current = cartAssetIdList;
      localStorage.setItem('CartAssetIds', JSON.stringify(cartAssetIdList));
      localStorage.setItem('CartAssetIdsMinutes', getCurrentMinutes().toString());
      syncSelectedAddonIdsForCartItems(cartItems, selectedAddonServiceIdsByAssetId);
      return;
    }

    if (!cartAssetIdList.length && cartAssetIds.current.length) {
      const fetchCartItems = async () => {
        const results = await Promise.all(
          cartAssetIds.current.map(async (id) => {
            const { data } = await assetsApi.getAssetDetail(id);
            if (data && data.status === 'OK') return data;
            return null;
          }),
        );

        const items = dedupeCartItemsByLocation(results.filter(Boolean));
        const storedAddonIds = loadSelectedAddonIdsFromStorage();

        if (items.length === 1) {
          dispatch(setAddCart(items[0]));
        } else if (items.length > 1) {
          dispatch(setCartItems(items));
        } else {
          localStorage.removeItem('CartAssetIds');
          localStorage.removeItem('CartSelectedAddonIds');
        }

        items.forEach((item: any) => {
          const assetId = String(item?.assetId ?? '');
          const serviceIds = storedAddonIds[assetId];
          if (!assetId || !serviceIds?.length) return;
          dispatch(setSelectedAddonServiceIdsForAsset({ assetId, serviceIds }));
        });
      };

      beginLoading();
      fetchCartItems()
        .catch(() => {
          localStorage.removeItem('CartAssetIds');
        })
        .finally(() => {
          endLoading();
        });
    }
  }, [
    cartAssetIdList,
    cartItems,
    enabled,
    dispatch,
    beginLoading,
    endLoading,
    selectedAddonServiceIdsByAssetId,
  ]);

  React.useEffect(() => {
    if (!enabled) return;

    const fetchEquipFee = async (id: string) => {
      const selectedServiceIds: number[] = selectedAddonServiceIdsByAssetId?.[id] || [];
      const { data } = await assetsApi.getBookingFee(
        id,
        pickupdate,
        pickupTime,
        returnDate,
        returnTime,
        discountCode,
        selectedServiceIds,
      );
      if (data && data.status === 'OK') {
        return data;
      }
      return null;
    };

    if (
      cartAssetIdList.length &&
      filterDate.pickupDate &&
      filterDate.returnDate &&
      ['/cart', '/checkout'].includes(pathname)
    ) {
      beginLoading();
      const requestId = ++feeRequestIdRef.current;
      Promise.all(cartAssetIdList.map((id: string) => fetchEquipFee(id)))
        .then((results) => {
          if (requestId !== feeRequestIdRef.current) return;

          const nextEquipFeeByAssetId = { ...equipFeeByAssetIdRef.current };
          cartAssetIdList.forEach((id: string, index: number) => {
            if (results[index]) {
              nextEquipFeeByAssetId[id] = results[index];
            }
          });
          dispatch(setEquipFeeByAssetId(nextEquipFeeByAssetId));

          if (pathname === '/checkout') {
            const checkoutItem = getSingleCheckoutItem(cartItems, selectedCheckoutLocationId);
            const checkoutItems = checkoutItem ? [checkoutItem] : [];
            dispatch(setEquipFee(getMergedEquipFeeForItems(checkoutItems, nextEquipFeeByAssetId)));
          }
        })
        .catch(() => {})
        .finally(() => {
          endLoading();
        });
    }
  }, [
    cartAssetIdList,
    cartItems,
    pickupdate,
    pickupTime,
    returnDate,
    returnTime,
    pathname,
    enabled,
    discountCode,
    selectedServiceIdsKey,
    selectedCheckoutLocationId,
    selectedAddonServiceIdsByAssetId,
    dispatch,
    beginLoading,
    endLoading,
  ]);
};
